import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql } from '@apollo/client/react/hoc';
import { compose } from 'recompose';

import queries from 'graph/queries';
import { carePlansIncomplete, taskStats } from 'graph/queries/carePlans';
import MARK_EXERCISE_AS_COMPLETE from 'graph/mutations/markExerciseAsComplete';
import { Video, ButtonKeetUI, Hexagon } from '@keethealth/ui-components';
import { Link } from 'react-router-dom';
import { getExerciseQuantities } from 'utils/content';
import Panel from 'common/Panel';
import CompletedModal from 'common/CompletedModal';
import history from 'utils/history';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import get from 'lodash/get';
import findIndex from 'lodash/findIndex';
import { getBasePath, findNext } from 'utils/utils';
import { isCompleted } from 'utils/carePlans';
import 'common/sceneBackNav.scss';
import './TaskDetail.scss';

const sceneBackNavLayout =
  'scene-back-nav task-detail-back-nav absolute left-2-ns left-1 top-0';

export function TaskDetail({
  loading,
  match,
  getNextActivity,
  exercise,
  exercises,
  markExerciseAsComplete,
  carePlanActivity,
  onBack,
  disableCompletion,
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [complete, setComplete] = useState(false);
  const [headerHeight, setHeaderHeight] = useState();
  const headerRef = useRef(null);
  const videoUrl = get(exercise, 'plannable.videoUrl');
  const {
    params: { taskId },
  } = match;

  useEffect(() => {
    if (exercise) {
      setComplete(isCompleted(exercise));
    }
  }, [exercise]);

  useLayoutEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    setHeaderHeight(header.offsetHeight);
  }, [loading, exercise?.plannable?.title]);

  return (
    <div data-testid="task-detail">
      {!loading && (
        <Panel className="overflow-auto">
          <div className="relative">
            <div
              ref={headerRef}
              className="f4 bb pa3 tc-ns tl pl5-ns pr5-ns task-detail-header"
            >
              <h2
                id="task-detail-title"
                className="f4 ma0 lh-title tc-ns tl pl5-ns pl4 pr5-ns task-detail-title"
                tabIndex={0}
              >
                {exercise.plannable ? exercise.plannable.title : null}
              </h2>
            </div>
            <div>
              <div className="flex flex-column items-center">
                {videoUrl && (
                  <Video url={videoUrl} controls className="ReactPlayer" />
                )}
                {renderQuantities()}
                {exercise.description && (
                  <section
                    className="measure-wide ma3 w-100 task-detail-instructions"
                    tabIndex={0}
                    aria-labelledby="task-detail-instructions-heading"
                  >
                    <h3 id="task-detail-instructions-heading" className="mt0 mb2">
                      INSTRUCTIONS
                    </h3>
                    <p className="ma0">{exercise.description}</p>
                  </section>
                )}
                {complete ? (
                  <div className="flex items-center flex-column mb5">
                    <div className="mb3">
                      <i className="primary f2 fas fa-check-circle" />
                    </div>
                    <div className="gray">Completed</div>
                  </div>
                ) : disableCompletion ? (
                  <div className="mb5" />
                ) : (
                  <ButtonKeetUI
                    type="button"
                    className="center mb5 mt3 task-detail-complete"
                    onClick={handleComplete}
                    disabled={complete}
                  >
                    Complete
                  </ButtonKeetUI>
                )}
              </div>
            </div>
            {!disableCompletion && (
              <CompletedModal isOpen={isModalOpen} onClose={handleEnd} />
            )}
            {onBack ? (
              <button
                type="button"
                className={`${sceneBackNavLayout} bn pa0 pointer`}
                style={
                  headerHeight != null ? { height: headerHeight } : undefined
                }
                onClick={onBack}
                aria-label="Back"
              >
                <i
                  className="fas fa-arrow-left scene-back-nav__icon"
                  aria-hidden="true"
                />
              </button>
            ) : (
              history.location.pathname.startsWith('/my_tasks') && (
                <Link
                  className={`${sceneBackNavLayout} bn pa0 pointer`}
                  style={
                    headerHeight != null ? { height: headerHeight } : undefined
                  }
                  to={getBasePath(history.location.pathname)}
                  aria-label="Back"
                >
                  <i
                    className="fas fa-arrow-left scene-back-nav__icon"
                    aria-hidden="true"
                  />
                </Link>
              )
            )}
          </div>
        </Panel>
      )}
    </div>
  );

  function getNext() {
    if (!isEmpty(exercises)) {
      const currentIndex = findIndex(exercises, { id: taskId });
      const nextActivity = findNext(
        exercises,
        currentIndex,
        (cpa) => cpa.status !== 'completed',
      );

      if (nextActivity) return nextActivity.id;
    }
    // return to beginning
    return null;
  }

  function handleEnd() {
    if (onBack && disableCompletion) {
      onBack();
      return;
    }
    let basePath = getBasePath(history.location.pathname);
    const nextActivityId = basePath.includes('my_tasks')
      ? getNextActivity()
      : getNext();

    if (nextActivityId) {
      basePath = `${basePath}/${nextActivityId}`;
      history.push(basePath);
      return;
    }
    if (onBack) {
      onBack();
      return;
    }
    history.push(basePath);
  }

  function handleComplete(e) {
    e.preventDefault();
    if (disableCompletion || !exercise?.id) return;
    const basePath = getBasePath(history.location.pathname);
    markExerciseAsComplete(exercise.id)
      .then(() => {
        const nextActivityId = disableCompletion
          ? null
          : basePath.includes('my_tasks')
            ? getNextActivity()
            : getNext();
        if (isEmpty(nextActivityId)) {
          setIsModalOpen(true);
        } else {
          handleEnd();
        }
      })
      .catch(() => {});
  }

  function renderQuantities() {
    const quantities = getExerciseQuantities(
      get(carePlanActivity, 'properties', {}),
    );

    return (
      <div className="w-100 pa3 bg-white bt bb">
        <div
          className="flex mw8 center flex justify-around taskDetails"
          role="presentation"
        >
          {map(quantities, (quantity) => {
            const displayValue = String(get(quantity, 'value') ?? '-');
            const ariaLabel = `${quantity.label}: ${displayValue}`;
            return (
              <div
                key={quantity.label}
                className="flex flex-column w-100 task-detail-stat"
                tabIndex={0}
                role="group"
                aria-label={ariaLabel}
              >
                <span aria-hidden="true">
                  <Hexagon
                    color="primary"
                    label={displayValue}
                  />
                </span>
                <span className="db tc mv2" aria-hidden="true">
                  {quantity.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}

TaskDetail.propTypes = {
  loading: PropTypes.bool.isRequired,
  match: PropTypes.object.isRequired,
  getNextActivity: PropTypes.func,
  exercise: PropTypes.shape({
    id: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    plannable: PropTypes.shape({
      videoUrl: PropTypes.string,
      title: PropTypes.string.isRequired,
    }),
  }),
  exercises: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      plannable: PropTypes.shape({
        videoUrl: PropTypes.string,
        title: PropTypes.string.isRequired,
      }),
    }),
  ),
  markExerciseAsComplete: PropTypes.func.isRequired,
  carePlanActivity: PropTypes.shape({
    properties: PropTypes.shape({
      count: PropTypes.number,
      duration: PropTypes.number,
      durationUnit: PropTypes.oneOf(['Second', 'Minute', 'Hour']),
      frequency: PropTypes.number,
      period: PropTypes.number,
      periodUnit: PropTypes.oneOf(['Hour', 'Day', 'Week']),
    }),
  }),
  onBack: PropTypes.func,
  disableCompletion: PropTypes.bool,
};

TaskDetail.defaultProps = {
  videoUrl: '',
  exercise: null,
  exercises: null,
  carePlanActivity: {
    properties: {},
  },
  getNextActivity: () => {},
  onBack: undefined,
  disableCompletion: false,
};

export default compose(
  graphql(MARK_EXERCISE_AS_COMPLETE, {
    props: ({ mutate }) => ({
      markExerciseAsComplete: (id) =>
        mutate({
          variables: { id },
          refetchQueries: [
            { query: carePlansIncomplete },
            { query: taskStats },
          ],
          awaitRefetchQueries: true,
        }),
    }),
  }),
  graphql(queries.exercise, {
    props: ({ data: { loading, exercise } }) => ({
      loading,
      exercise,
    }),
    options: ({
      match: {
        params: { taskId: id },
      },
    }) => ({
      variables: { id },
      fetchPolicy: 'cache-and-network',
    }),
  }),
  graphql(queries.exercises, {
    options: { fetchPolicy: 'cache-and-network' },
    props: ({ data: { exercises } }) => ({
      exercises,
    }),
  }),
)(TaskDetail);
